import React, { useEffect, useState } from 'react'
import axios from "axios";
import { api_url } from "../Hooks/Global";
import Swal from 'sweetalert2';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

function StudentPRN() {
    const { enrollment_number } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [prn, setPrn] = useState('');
    const [loading, setLoading] = useState(false);
    const [verificationData, setVerificationData] = useState(null);
    const [isVerified, setIsVerified] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const queryPrn = queryParams.get("pnr");

        const finalPRN = enrollment_number || queryPrn;

        if (finalPRN) {
            setPrn(finalPRN);
            handleVerify(finalPRN);
        }

    }, [enrollment_number, location.search]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 0:
                return <span className="badge bg-warning">Pending</span>;
            case 1:
                return <span className="badge bg-info">Enrolled</span>;
            case 3:
                return <span className="badge bg-success">Verified</span>;
            default:
                return <span className="badge bg-secondary">Unknown</span>;
        }
    };

    const handleVerify = async (prnValue = null) => {
        const enrollmentNo = prnValue || prn;

        if (!enrollmentNo || enrollmentNo.trim() === '') {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Input',
                text: 'Please enter a valid PNR/Enrollment Number',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(`${api_url}Masters/GetStudentVerification`, {
                params: { enrollment_number: enrollmentNo }
            });

            const { success, data, message } = response.data;

            if (success && data) {
                setVerificationData(data);
                setIsVerified(true);
                setResponseMessage(message || '');
            } else {
                setVerificationData(null);
                setIsVerified(false);
                setResponseMessage(message || 'No student found with this PNR/Enrollment Number');
                Swal.fire({
                    icon: 'error',
                    title: 'Verification Failed',
                    text: message || 'No student found with this PNR/Enrollment Number',
                    confirmButtonColor: '#3085d6'
                });
            }
        } catch (error) {
            console.error("Error verifying student:", error);
            setVerificationData(null);
            setIsVerified(false);
            setResponseMessage(error.response?.data?.message || 'Failed to verify student. Please try again later.');
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to verify student. Please try again later.',
                confirmButtonColor: '#3085d6'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleVerify();
    };

    const handleInputChange = (e) => {
        setPrn(e.target.value);
        if (isVerified) {
            setIsVerified(false);
            setVerificationData(null);
        }
        setResponseMessage('');
    };

    return (
        <>
            <div className="inner-page-banner-area" style={{ backgroundImage: 'url("/images/About_images/about_banner.jpg")' }}>
                <div className="container">
                    <div className="pagination-area">
                        <h1>Student Verification</h1>
                    </div>
                </div>
            </div>

            <div className='container'>
                <div className='row d-flex justify-content-center'>
                    <div className='col-md-5'>
                        <div className='card rounded rounded-5 text-light shadow my-4 p-4 bg-primary'>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label text-light fs-6 fw-bold">Enter PNR / Enrollment Number</label>
                                    <div className='d-flex'>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder='Type here...'
                                            value={prn}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        <button
                                            type="submit"
                                            className="btn btn-warning ms-2"
                                            disabled={loading}
                                        >
                                            {loading ? 'Verifying...' : 'Verify'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary ms-2"
                                            onClick={() => {
                                                setPrn('');
                                                setIsVerified(false);
                                                setVerificationData(null);
                                                setResponseMessage('');
                                            }}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                        {responseMessage && (
                            <div className="mb-2 text-center">
                                <h3 className={`fw-bold mb-0 text-${isVerified ? 'success' : 'danger'}`}>{responseMessage}</h3>
                            </div>
                        )}
                    </div>
                </div>

                {isVerified && verificationData && (
                    <div className='row d-flex justify-content-center mb-5'>
                        <div className='col-md-8'>
                            <div className='card shadow'>
                                <div className='card-header bg-primary d-flex justify-content-between align-items-center'>
                                    <h4 className='mb-0 text-white'>Student Details</h4>

                                    <p className='mb-0'>
                                        {getStatusBadge(verificationData.students_programs_status)}
                                    </p>
                                </div>
                                <div className='card-body'>
                                    <div className='row'>
                                        <div className='col-md-6 mb-3'>
                                            <label className='fw-bold'>Enrollment/PNR Number:</label>
                                            <p className='mb-0'>{verificationData.enrollment_number || prn}</p>
                                        </div>
                                        <div className='col-md-6 mb-3'>
                                            <label className='fw-bold'>Student Full Name:</label>
                                            <p className='mb-0'>{verificationData.student_full_name || 'N/A'}</p>
                                        </div>
                                        <div className='col-md-6 mb-3'>
                                            <label className='fw-bold'>Franchise Name:</label>
                                            <p className='mb-0'>{verificationData.franchise_name || 'N/A'}</p>
                                        </div>
                                        <div className='col-md-6 mb-3'>
                                            <label className='fw-bold'>Franchise District:</label>
                                            <p className='mb-0'>{verificationData.franchise_district || 'N/A'}</p>
                                        </div>
                                        <div className='col-md-6 mb-3'>
                                            <label className='fw-bold'>Franchise State:</label>
                                            <p className='mb-0'>{verificationData.franchise_state || 'N/A'}</p>
                                        </div>
                                        <div className='col-md-6 mb-3'>
                                            <label className='fw-bold'>Program:</label>
                                            <p className='mb-0'>{verificationData.program_title || 'N/A'}</p>
                                        </div>
                                        {
                                            verificationData.publish_date && (
                                                <div className='col-md-6 mb-3'>
                                                    <label className='fw-bold'>Publish Date:</label>
                                                    <p className='mb-0'>{verificationData.publish_date || 'N/A'}</p>
                                                </div>
                                            )
                                        }
                                        {verificationData.passing_month && verificationData.passing_year && (
                                            <div className='col-md-6 mb-3'>
                                                <label className='fw-bold'>Passing Month/Year:</label>
                                                <p className='mb-0'>{`${verificationData.passing_month || 'N/A'}, ${verificationData.passing_year || 'N/A'}`}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default StudentPRN